/**
 * SldSymbolNode — a React Flow custom node that renders one of our symbols.
 *
 * Each symbol's content is inline SVG. Each terminal becomes a React Flow
 * <Handle>, giving us:
 *   - Snap-to-terminal during drag (React Flow handles this)
 *   - Connection validation via isValidConnection (we enforce types)
 *   - Wire endpoints that follow the symbol through drag / rotation
 */
import React, { memo, useMemo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, useNodeConnections, useUpdateNodeInternals } from '@xyflow/react';
import { useEditorStore, type SldNode } from '../store/editor';
import { SYMBOLS } from '../symbols/library';
import { TYPE_COLORS, terminalTooltip } from '../types/symbol';

/**
 * Derive React Flow Position (top/right/bottom/left) from a terminal's
 * native coordinates, accounting for rotation. React Flow uses this to
 * orient connection paths.
 */
function terminalPosition(
  termX: number,
  termY: number,
  nativeW: number,
  nativeH: number,
  rotation: 0 | 90 | 180 | 270
): Position {
  // Determine which edge the terminal is nearest in native coords
  const epsilon = 0.1;
  let nativePos: Position;
  if (termY < epsilon) nativePos = Position.Top;
  else if (termY > nativeH - epsilon) nativePos = Position.Bottom;
  else if (termX < epsilon) nativePos = Position.Left;
  else if (termX > nativeW - epsilon) nativePos = Position.Right;
  else nativePos = Position.Top; // fallback

  // Rotate by the symbol's rotation
  const order = [Position.Top, Position.Right, Position.Bottom, Position.Left];
  const idx = order.indexOf(nativePos);
  const rotSteps = rotation / 90;
  return order[(idx + rotSteps) % 4];
}

/**
 * Compute on-screen terminal coordinates as ABSOLUTE PIXELS in the
 * node's local coordinate space, accounting for rotation.
 */
function terminalCssPosition(
  termX: number,
  termY: number,
  nativeW: number,
  nativeH: number,
  rotation: 0 | 90 | 180 | 270,
  scale: number
): { left: string; top: string } {
  const cx = nativeW / 2;
  const cy = nativeH / 2;
  const rx = termX - cx;
  const ry = termY - cy;
  const theta = (rotation * Math.PI) / 180;
  const rrx = rx * Math.cos(theta) - ry * Math.sin(theta);
  const rry = rx * Math.sin(theta) + ry * Math.cos(theta);

  const rotatedW = rotation === 90 || rotation === 270 ? nativeH : nativeW;
  const rotatedH = rotation === 90 || rotation === 270 ? nativeW : nativeH;

  const finalX = (rrx + rotatedW / 2) * scale;
  const finalY = (rry + rotatedH / 2) * scale;

  return {
    left: `${finalX}px`,
    top:  `${finalY}px`
  };
}

export const SldSymbolNode = memo((props: NodeProps<SldNode>) => {
  const { id, data, selected } = props;
  const sym = SYMBOLS[data.symbolId];

  // Connections to THIS node — used to (a) blend handle visuals with
  // the connected wire colour, and (b) draw a junction dot when 3+
  // wires meet at a single terminal (standard schematic convention).
  const connections = useNodeConnections({ id });
  const perTerminalCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of connections) {
      if (c.source === id && c.sourceHandle) {
        counts.set(c.sourceHandle, (counts.get(c.sourceHandle) ?? 0) + 1);
      }
      if (c.target === id && c.targetHandle) {
        counts.set(c.targetHandle, (counts.get(c.targetHandle) ?? 0) + 1);
      }
    }
    return counts;
  }, [connections, id]);

  if (!sym) return <div style={{ color: 'red' }}>Unknown symbol: {data.symbolId}</div>;

  const rotation = data.rotation ?? 0;

  // Tell React Flow to re-measure this node's handle positions whenever
  // rotation changes. Without this, RF caches the handle positions from
  // first render and only re-measures when the node's bounding box
  // changes — so for square symbols (meter, grid) or any rotation that
  // doesn't swap W/H, wires stay anchored to where the terminals USED to
  // be even though the handles have moved in the DOM.
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [rotation, id, updateNodeInternals]);
  const scale = sym.defaultScale;

  // Is this symbol geometrically INSIDE an existing-wrapper annotation?
  // If yes, suppress its own dashed boundary so the wrapper's box is
  // the only visual marker (no double-marking). Centre-point test
  // against the wrapper's bbox — cheap and intuitive (a symbol is
  // "inside" if its centre falls within the wrapper).
  const insideExistingWrapper = useEditorStore((s) => {
    if (!data.isExisting) return false;
    const me = s.nodes.find((n) => n.id === id);
    if (!me) return false;
    const myW = (me.data?.customBox?.w ?? sym.nativeBox.w) * scale;
    const myH = (me.data?.customBox?.h ?? sym.nativeBox.h) * scale;
    const cx = me.position.x + myW / 2;
    const cy = me.position.y + myH / 2;
    return s.nodes.some((n) => {
      if (!n.id.startsWith('existing-boundary-')) return false;
      const wW = n.data?.customBox?.w ?? 0;
      const wH = n.data?.customBox?.h ?? 0;
      return cx >= n.position.x
          && cy >= n.position.y
          && cx <= n.position.x + wW
          && cy <= n.position.y + wH;
    });
  });

  const terminals = data.customTerminals ?? sym.terminals;

  const nativeW = data.customBox?.w ?? sym.nativeBox.w;
  const nativeH = data.customBox?.h ?? sym.nativeBox.h;
  const content = data.customContent ?? sym.content;

  const scaledW = nativeW * scale;
  const scaledH = nativeH * scale;

  const displayW = rotation === 90 || rotation === 270 ? scaledH : scaledW;
  const displayH = rotation === 90 || rotation === 270 ? scaledW : scaledH;

  return (
    <div
      // Belt-and-braces selection. React Flow's onNodeClick normally
      // handles selection, but if the user happens to pointerdown on a
      // terminal Handle (or hovers near one — handles grow on hover),
      // RF treats it as a connection-handle gesture and the click never
      // reaches the node, so the first click "does nothing." Forcing
      // selection on pointerdown here covers that case: even if the
      // gesture later resolves to a wire-drag from a handle, having the
      // owning symbol selected first is the correct UX.
      onPointerDown={() => {
        if (id === '__page_frame__') return;
        useEditorStore.getState().setSelectedNodeId(id);
      }}
      style={{
        position: 'relative',
        width: displayW,
        height: displayH,
        boxSizing: 'border-box',
        // The selection ring is now drawn by CSS on
        // .react-flow__node-sldSymbol.selected — gives us a softer
        // double-layer halo that matches the rest of the UI. We keep
        // a tiny radius here so the halo's corners look right.
        borderRadius: 3,
        // Wrapper annotations (EXISTING boxes) are large bounding
        // rectangles that visually OVERLAP the symbols inside them.
        // We make their interior transparent to clicks so users can
        // still grab the underlying inverter / panel string / etc.
        // The dashed border + EXISTING badge stay clickable via the
        // SVG's `pointer-events: visiblePainted` (only painted regions
        // intercept events; the very faint fill is excluded by setting
        // its opacity to a tiny value).
        pointerEvents: id.startsWith('existing-boundary-') ? 'none' : undefined,
      }}
    >
      {/* EXISTING annotation — universal across all symbol types.
          Dashed violet boundary + small "EXISTING" pill top-right.
          Suppressed when this symbol is geometrically INSIDE an
          existing-wrapper annotation node (created by the multi-select
          "Wrap selection" action) — the wrapper provides the visual,
          drawing per-symbol boxes too would be redundant double-marking.
          See InsideExistingWrapperGuard below. */}
      {data.isExisting && !insideExistingWrapper && (
        <>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: -6,
              border: '1.4px dashed #7c3aed',
              borderRadius: 4,
              background: 'rgba(124, 58, 237, 0.04)',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -10,
              right: -4,
              padding: '1px 6px',
              background: '#7c3aed',
              color: '#ffffff',
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: 0.6,
              borderRadius: 2,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              pointerEvents: 'none',
              zIndex: 2,
              boxShadow: '0 1px 2px rgba(124, 58, 237, 0.35)',
            }}
          >
            EXISTING
          </div>
        </>
      )}
      <svg
        width={displayW}
        height={displayH}
        viewBox={`0 0 ${displayW} ${displayH}`}
        style={{
          overflow: 'visible',
          // For wrapper annotations the outer div has pointerEvents:none
          // (interior pass-through) — but we WANT the dashed border + the
          // EXISTING badge inside this SVG to still capture clicks so the
          // user can select / drag / delete the wrapper. visiblePainted
          // gives us "only painted regions intercept events", so the
          // mostly-transparent fill stays click-through while the stroke
          // and the solid badge become the wrapper's hit target.
          pointerEvents: id.startsWith('existing-boundary-') ? 'visiblePainted' : 'none',
          // display:block kills the inline baseline gap that otherwise
          // shifts the SVG content down by a few pixels.
          display: 'block'
        }}
      >
        <g
          transform={`
            translate(${displayW / 2}, ${displayH / 2})
            rotate(${rotation})
            translate(${-scaledW / 2}, ${-scaledH / 2})
            scale(${scale})
          `}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </svg>

      {Object.values(terminals).map((term) => {
        const pos = terminalPosition(term.x, term.y, nativeW, nativeH, rotation);
        const css = terminalCssPosition(term.x, term.y, nativeW, nativeH, rotation, scale);
        const color = TYPE_COLORS[term.type];
        const wireCount = perTerminalCount.get(term.id) ?? 0;
        const isConnected = wireCount > 0;
        const isJunction  = wireCount >= 3;
        const handleStyle: React.CSSProperties = {
          ...css,
          color,
          transform: 'translate(-50%, -50%)'
        };
        // Friendly label rendered as a native browser tooltip on
        // hover. Helps users (especially new ones) tell apart the
        // 3 terminals on a hybrid inverter, the +/− on a battery,
        // or the unused dc_in on the embedded-micros symbol.
        const tip = terminalTooltip(term.id, term.type);
        return (
          <React.Fragment key={term.id}>
            <Handle
              id={term.id}
              type="source"
              position={pos}
              style={handleStyle}
              data-type={term.type}
              data-connected={isConnected ? '1' : undefined}
              title={tip}
            />
            <Handle
              id={term.id}
              type="target"
              position={pos}
              style={handleStyle}
              data-type={term.type}
              data-connected={isConnected ? '1' : undefined}
              title={tip}
            />
            {/* Junction dot — filled circle at the terminal centre when 3+
                wires meet. Standard schematic convention: dot = electrical
                connection, no dot = crossing. */}
            {isJunction && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  left: css.left,
                  top: css.top,
                  width: 6,
                  height: 6,
                  background: '#0f172a',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  zIndex: 5
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      {(data.label || data.sublabel) && (
        <EditableLabel
          nodeId={id}
          label={data.label ?? ''}
          sublabel={data.sublabel ?? ''}
        />
      )}
    </div>
  );
});

/**
 * Renders the symbol's label / sublabel and turns into an inline
 * <input> on double-click. Pushes a single history snapshot when the
 * user starts editing (not per keystroke), so Cmd/Ctrl+Z reverts the
 * whole edit at once. Enter/blur commits, Escape discards.
 */
function EditableLabel({
  nodeId, label, sublabel
}: { nodeId: string; label: string; sublabel: string }) {
  const setNodes    = useEditorStore((s) => s.setNodes);
  const pushHistory = useEditorStore((s) => s.pushHistory);

  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    pushHistory();
    setDraft(label);
    setEditing(true);
  };

  const commit = () => {
    if (draft !== label) {
      setNodes((prev) =>
        prev.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, label: draft } } : n
        )
      );
    }
    setEditing(false);
  };
  const cancel = () => {
    setDraft(label);
    setEditing(false);
  };

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: 4,
    fontSize: 10,
    fontWeight: 600,
    color: '#0f172a',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  };

  if (editing) {
    return (
      <div style={wrapperStyle}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter')  { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            e.stopPropagation();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            font: 'inherit',
            color: 'inherit',
            background: '#fff',
            border: '1px solid #2563eb',
            borderRadius: 3,
            padding: '1px 4px',
            outline: 'none',
            minWidth: 80,
            textAlign: 'center'
          }}
        />
        {sublabel && (
          <div style={{ fontSize: 9, fontWeight: 400, color: '#64748b', marginTop: 1 }}>
            {sublabel}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{ ...wrapperStyle, cursor: 'text', pointerEvents: 'all' }}
      onDoubleClick={startEdit}
      title="Double-click to edit"
    >
      {label}
      {sublabel && (
        <div style={{ fontSize: 9, fontWeight: 400, color: '#64748b', marginTop: 1 }}>
          {sublabel}
        </div>
      )}
    </div>
  );
}
SldSymbolNode.displayName = 'SldSymbolNode';
